<template>
  <div class="p-3 flex flex-col bg-key rounded-4xl">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-1 h-9 text-black-white text-l-medium">
        <Icon icon="Function-Wireframe-Clock" class="size-6" />
        <span class="text-l-medium text-black-white">{{
          t('setting.device.wirelessSleepTime.title')
        }}</span>
        <Tooltip side="right" :side-offset="-6">
          <Icon
            icon="Function-Wireframe-Description"
            class="text-text-glass-default size-4.5 outline-none"
          />
          <template #content>
            <div
              class="max-w-60 whitespace-normal wsarp-break-words text-s-medium text-white-black p-1 bg-floating-tooltip-container rounded-m"
            >
              {{ t('setting.device.wirelessSleepTime.tooltipContent') }}
            </div>
          </template>
        </Tooltip>
      </div>
      <div>
        <Tooltip
          side="left"
          :content="t('setting.device.wirelessSleepTime.disableSleepTooltip', { device: '鼠标' })"
        >
          <div>
            <Switch v-model="isAllowSleep" size="lg" />
          </div>
        </Tooltip>
      </div>
    </div>
    <div>
      <p class="text-m-medium text-text-glass-default">
        {{ t('setting.device.wirelessSleepTime.description', { device: '鼠标' }) }}
      </p>
    </div>
    <div class="mt-1 pt-6 pb-4 w-full flex items-center justify-between">
      <div class="w-full flex items-center gap-4 text-m-medium">
        <span :class="!isAllowSleep ? 'text-text-disabled' : 'text-text-glass-default'">
          {{ min }}{{ t('setting.device.wirelessSleepTime.minuteUnit') }}
        </span>
        <div class="relative flex-1">
          <Slider
            v-model="currentSleepTime"
            :min="min"
            :max="max"
            :step="1"
            :disabled="!isAllowSleep"
            thumb-alignment="overflow"
            class="w-full"
            track-class="h-3 bg-neutral-light-default"
            range-class="bg-brand-blue1"
            :thumb-class="`size-4.5 ${!isAllowSleep ? 'bg-blue-light-default' : 'bg-blue-dark-hover'}`"
            @value-commit="commitSleepTime"
          />
          <div
            class="absolute bottom-4 left-0 -translate-x-1/2 pointer-events-none text-center leading-none whitespace-nowrap"
            :style="{ left: bubbleLeft }"
          >
            <div
              class="text-m-medium"
              :class="!isAllowSleep ? 'text-text-disabled' : 'text-brand-blue1'"
            >
              {{ currentSleepTime[0] }}{{ t('setting.device.wirelessSleepTime.minuteUnit') }}
            </div>
          </div>
        </div>
        <span :class="!isAllowSleep ? 'text-text-disabled' : 'text-text-glass-default'">
          {{ max }}{{ t('setting.device.wirelessSleepTime.minuteUnit') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { Icon, Switch, Slider, Tooltip } from '@rapoo/element'
  import { useDeviceStore, useLocaleStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import type { ParametersCapability } from '@rapoo/device-sdk'
  import type { ParametersContext } from '../../composables/useParameters'

  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const props = defineProps<{
    parameters: ParametersContext
  }>()
  const { state, actions } = props.parameters

  const sleepTimeRange = computed(() => {
    const capability = currentDevice.value?.manifest.capabilities?.find(
      (item): item is ParametersCapability => item.type === 'parameters',
    )
    return capability?.config.sleepTimeRange
  })
  const min = computed(() => sleepTimeRange.value?.min ?? 2)
  const max = computed(() => sleepTimeRange.value?.max ?? 120)

  // Switch 开启 = 允许休眠；Switch 关闭 = 永不休眠（值为 255）
  const isAllowSleep = computed({
    get: () => state.sleepTime.value !== 255,
    set: (val: boolean) => {
      if (val) {
        // 开启允许休眠，恢复为默认值 10
        actions.setSleepTime(10)
      } else {
        // 关闭允许休眠 = 永不休眠
        actions.setSleepTime(255)
      }
    },
  })

  // 实时跟踪拖动位置（bubble 跟随用）
  const currentSleepTime = ref([state.sleepTime.value === 255 ? 10 : state.sleepTime.value])

  // 当 SDK 值变化时同步到 slider（非永不休眠时）
  watch(
    () => state.sleepTime.value,
    (val) => {
      if (val !== 255) {
        currentSleepTime.value = [val]
      }
    },
    { immediate: true },
  )

  const bubbleLeft = computed(() => {
    const ratio = (currentSleepTime.value[0] - min.value) / (max.value - min.value)
    const progress = Math.min(1, Math.max(0, ratio))
    return `${progress * 100}%`
  })

  // 仅在拖动释放时提交值
  const commitSleepTime = (value: number[]) => {
    actions.setSleepTime(value[0])
  }
</script>
